import ImageWithFallback from "@/components/Image";
import NumberFireIcon from "@/components/svg/NumberFireIcon";
import type { Team } from "../types";
import styles from "./leaders.module.scss";

interface LeadersProps {
  homeTeam?: Team | null;
  awayTeam?: Team | null;
  isTooSmallForFullNames: boolean;
}

interface LeaderNameAndLogoProps {
  teamName?: string | null;
  logo?: string | null;
  leaderName?: string | null;
  isTooSmallForFullNames: boolean;
}
const LeaderNameAndLogo = ({
  teamName,
  logo,
  leaderName,
  isTooSmallForFullNames,
}: LeaderNameAndLogoProps) => {
  return (
    <div className={styles.leaderNameAndLogo}>
      {logo && (
        <ImageWithFallback
          src={logo}
          width={16}
          height={16}
          alt={teamName ? `Logo for ${teamName}` : "Away team logo"}
        />
      )}
      <div className={styles.leaderName}>
        {condenseName(leaderName ?? "", isTooSmallForFullNames)}
      </div>
    </div>
  );
};

const condenseName = (
  name: string,
  isTooSmallForFullNames: boolean
): string => {
  const nameSplit = name.split(" ");
  if (nameSplit.length < 2) {
    return "-";
  }
  const [firstName, lastName] = nameSplit;
  const characterLimit = isTooSmallForFullNames ? 8 : 14;
  const limitedLastName =
    lastName.length > characterLimit
      ? `${lastName.slice(0, characterLimit - 1)}...`
      : lastName;
  return `${firstName[0]}. ${limitedLastName}`;
};

const Leaders = ({
  homeTeam,
  awayTeam,
  isTooSmallForFullNames,
}: LeadersProps) => {
  const homeLeaders = homeTeam?.leaders;
  const awayLeaders = awayTeam?.leaders;

  return (
    <div className={styles.container}>
      <div className={styles.containerHeading}>
        <NumberFireIcon width={14} height={18} />
        <h3>Game Leaders</h3>
      </div>
      <div className={styles.leaderCards}>
        <div className={styles.leaderBox}>
          <div className={styles.leaderHeading}>
            <h4>Passing Leaders</h4>
          </div>
          <table className={styles.leaderTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>CMP/ATT</th>
                <th>YDS</th>
                <th>TD</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <LeaderNameAndLogo
                    teamName={awayTeam?.name}
                    logo={awayTeam?.logo}
                    leaderName={awayLeaders?.passing?.name}
                    isTooSmallForFullNames={isTooSmallForFullNames}
                  />
                </td>
                <td>{`${awayLeaders?.passing?.completions ?? "-"}/${awayLeaders?.passing?.attempts ?? "-"}`}</td>
                <td>{awayLeaders?.passing?.yards ?? "-"}</td>
                <td>{awayLeaders?.passing?.touchdowns ?? "-"}</td>
              </tr>
              <tr>
                <td>
                  <LeaderNameAndLogo
                    teamName={homeTeam?.name}
                    logo={homeTeam?.logo}
                    leaderName={homeLeaders?.passing?.name}
                    isTooSmallForFullNames={isTooSmallForFullNames}
                  />
                </td>
                <td>{`${homeLeaders?.passing?.completions ?? "-"}/${homeLeaders?.passing?.attempts ?? "-"}`}</td>
                <td>{homeLeaders?.passing?.yards ?? "-"}</td>
                <td>{homeLeaders?.passing?.touchdowns ?? "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className={styles.leaderBox}>
          <div className={styles.leaderHeading}>
            <h4>Rushing Leaders</h4>
          </div>
          <table className={styles.leaderTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>ATT</th>
                <th>YDS</th>
                <th>TD</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <LeaderNameAndLogo
                    teamName={awayTeam?.name}
                    logo={awayTeam?.logo}
                    leaderName={awayLeaders?.rushing?.name}
                    isTooSmallForFullNames={isTooSmallForFullNames}
                  />
                </td>
                <td>{awayLeaders?.rushing?.carries ?? "-"}</td>
                <td>{awayLeaders?.rushing?.yards ?? "-"}</td>
                <td>{awayLeaders?.rushing?.touchdowns ?? "-"}</td>
              </tr>
              <tr>
                <td>
                  <LeaderNameAndLogo
                    teamName={homeTeam?.name}
                    logo={homeTeam?.logo}
                    leaderName={homeLeaders?.rushing?.name}
                    isTooSmallForFullNames={isTooSmallForFullNames}
                  />
                </td>
                <td>{homeLeaders?.rushing?.carries ?? "-"}</td>
                <td>{homeLeaders?.rushing?.yards ?? "-"}</td>
                <td>{homeLeaders?.rushing?.touchdowns ?? "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className={styles.leaderBox}>
          <div className={styles.leaderHeading}>
            <h4>Receiving Leaders</h4>
          </div>
          <table className={styles.leaderTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>REC/TGT</th>
                <th>YDS</th>
                <th>TD</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <LeaderNameAndLogo
                    teamName={awayTeam?.name}
                    logo={awayTeam?.logo}
                    leaderName={awayLeaders?.receiving?.name}
                    isTooSmallForFullNames={isTooSmallForFullNames}
                  />
                </td>
                <td>{`${awayLeaders?.receiving?.receptions ?? "-"}/${awayLeaders?.receiving?.targets ?? "-"}`}</td>
                <td>{awayLeaders?.receiving?.yards ?? "-"}</td>
                <td>{awayLeaders?.receiving?.touchdowns ?? "-"}</td>
              </tr>
              <tr>
                <td>
                  <LeaderNameAndLogo
                    teamName={homeTeam?.name}
                    logo={homeTeam?.logo}
                    leaderName={homeLeaders?.receiving?.name}
                    isTooSmallForFullNames={isTooSmallForFullNames}
                  />
                </td>
                <td>{`${homeLeaders?.receiving?.receptions ?? "-"}/${homeLeaders?.receiving?.targets ?? "-"}`}</td>
                <td>{homeLeaders?.receiving?.yards ?? "-"}</td>
                <td>{homeLeaders?.receiving?.touchdowns ?? "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Leaders;
